import { useState } from 'react';
import { comparerParNomEcrit } from '../../../server/lib/personnes';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { congesApi, type FiltresRegistre } from '../../services/conges-api';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { BoutonSecondaire, Case, Champ, Saisie, Selection } from '../../components/ndf/ui/Champ';
import { COULEUR_STATUT, LIBELLE_STATUT, dateFr } from './CongesDemandesPage';
import { periodeFr } from '../../lib/periode-conges';
import { jourAParis } from '../../../server/lib/dates';

/**
 * Le registre des demandes (R-CAL-05).
 *
 * Il n'existait AUCUN endroit pour consulter une demande déjà tranchée : la
 * file « À valider » ne montre que l'attente, le calendrier ne montre qu'un
 * mois. L'annulation RH d'une absence approuvée (T7) était donc inatteignable
 * dans l'interface — l'action existait côté serveur, mais rien n'y menait.
 *
 * C'est aussi le registre qui remplace Airtable : on y cherche une absence par
 * personne, par type, par période, ou par ce qui reste à régulariser en paie.
 *
 * Le périmètre vient du serveur : la RH voit tout le monde, le manager son
 * équipe. Le titre s'ajuste, mais rien n'est filtré à l'écran.
 */

const STATUTS = [
  'attente_manager',
  'attente_rh',
  'approuvee',
  'refusee',
  'annulee',
];

export function CongesRegistrePage() {
  const navigate = useNavigate();
  const [filtres, setFiltres] = useState<FiltresRegistre>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'registre', filtres],
    queryFn: () => congesApi.demandes.registre(filtres),
  });
  const { data: types } = useQuery({
    queryKey: ['conges', 'types-absence'],
    queryFn: () => congesApi.typesAbsence.liste(),
    retry: false,
  });

  const demandes = data?.demandes ?? [];
  const personnes = [
    ...new Map(demandes.map((d) => [d.salarie_id, d.salarie_nom])).entries(),
    // Ordre d'annuaire : le nom rendu par le serveur est « Prénom NOM », le
    // trier tel quel reviendrait à classer par prénom.
  ].sort((a, b) => comparerParNomEcrit(a[1], b[1]));

  const modifier = (champ: keyof FiltresRegistre, valeur: string | boolean) =>
    setFiltres((f) => ({ ...f, [champ]: valeur === '' ? undefined : valeur }));

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-titre text-xl font-bold text-nuit">Demandes</h1>
          <p className="text-sm text-nuit/60">
            Toutes les demandes de votre périmètre, traitées comprises.
          </p>
        </div>
        {Object.values(filtres).some(Boolean) && (
          <BoutonSecondaire type="button" onClick={() => setFiltres({})}>
            Effacer les filtres
          </BoutonSecondaire>
        )}
      </header>

      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-3">
        <Champ label="Personne">
          <Selection
            value={filtres.salarie_id ?? ''}
            onChange={(e) => modifier('salarie_id', e.target.value)}
          >
            <option value="">Tout le monde</option>
            {personnes.map(([id, nom]) => (
              <option key={id} value={id}>
                {nom}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Type d’absence">
          <Selection value={filtres.type_id ?? ''} onChange={(e) => modifier('type_id', e.target.value)}>
            <option value="">Tous les types</option>
            {(types?.types ?? [])
              .slice()
              .sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'))
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.libelle}
                </option>
              ))}
          </Selection>
        </Champ>
        <Champ label="Statut">
          <Selection value={filtres.statut ?? ''} onChange={(e) => modifier('statut', e.target.value)}>
            <option value="">Tous les statuts</option>
            {STATUTS.map((s) => (
              <option key={s} value={s}>
                {LIBELLE_STATUT[s] ?? s}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="À partir du">
          <Saisie
            type="date"
            value={filtres.debut ?? ''}
            onChange={(e) => modifier('debut', e.target.value)}
          />
        </Champ>
        <Champ label="Jusqu’au">
          <Saisie type="date" value={filtres.fin ?? ''} onChange={(e) => modifier('fin', e.target.value)} />
        </Champ>
        <div className="flex items-end">
          <Case
            label="À régulariser en paie"
            aide="Les absences déclarées après coup, ou annulées après transmission."
            checked={Boolean(filtres.a_regulariser)}
            onChange={(e) => modifier('a_regulariser', e.target.checked)}
          />
        </div>
      </div>

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      <p className="text-sm text-nuit/60">{demandes.length} demande(s).</p>

      {demandes.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-nuit/60">
              <tr>
                <th className="px-3 py-2 font-medium">Personne</th>
                <th className="w-48 px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Période</th>
                <th className="px-3 py-2 text-right font-medium">Jours</th>
                <th className="px-3 py-2 font-medium">Statut</th>
                <th className="px-3 py-2 font-medium">Décidée le</th>
              </tr>
            </thead>
            <tbody>
              {demandes.map((d) => (
                <tr
                  key={d.id}
                  // La LIGNE ENTIÈRE ouvre le détail : n'ouvrir que sur le nom
                  // obligeait à chercher le pixel qui répond.
                  onClick={() => navigate(`/conges/demandes/${d.id}?depuis=/conges/registre`)}
                  className="cursor-pointer border-t border-slate-100 hover:bg-ciel/10"
                >
                  <td className="px-3 py-2 text-nuit">{d.salarie_nom}</td>
                  <td className="max-w-48 truncate px-3 py-2 text-nuit/80">
                    {d.type_libelle}
                    {d.origine === 'migration_airtable' && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                        reprise
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-nuit/70">{periodeFr(d)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-nuit/70">{d.nb_jours}</td>
                  <td className="px-3 py-2">
                    {/* Les badges ne se coupent pas : « à régulariser » sur deux
                        lignes se lit comme deux étiquettes différentes. */}
                    <span
                      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
                        COULEUR_STATUT[d.statut] ?? 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {LIBELLE_STATUT[d.statut] ?? d.statut}
                    </span>
                    {d.a_regulariser_paie && (
                      <span className="ml-1 inline-block whitespace-nowrap rounded-full bg-jaune-fond/40 px-2 py-0.5 text-xs font-medium text-jaune">
                        à régulariser
                      </span>
                    )}
                    {d.a_posteriori && (
                      <span className="ml-1 inline-block whitespace-nowrap rounded-full bg-jaune-fond/40 px-2 py-0.5 text-xs font-medium text-jaune">
                        a posteriori
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-nuit/60">
                    {/* `decidee_at` est un HORODATAGE : le découper lirait le jour
                        en temps universel, et une décision prise à 00h30 heure de
                        Paris serait datée de la veille dans le registre. */}
                    {d.decidee_at ? dateFr(jourAParis(new Date(d.decidee_at))) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && demandes.length === 0 && (
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-nuit/60">
          Aucune demande ne correspond à ces filtres.
        </p>
      )}
    </div>
  );
}
